import { memo, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import CopyCode from 'features/licenses/components/LicensesGroupsTable/CopyCode'
import { getSortOptions, sortByDate } from 'components/Table/utils'
import { DateCell } from 'components/Table'
import type { LicenseType } from 'features/licenses/types'
import { homeLicensingMessages } from '../../messages'
import HomeLicensesGroup from './HomeLicensesGroup'
import AvailableLicenseNote from './AvailableLicenseNote'
import ApplyLicense from './ApplyLicense'
import { HomeLicensesApplyCellStyles, CellStyles } from './styles'

interface HomeLicensesGroupsTableProps {
  licensesGroups: LicenseType[][]
}

const DEFAULT_SORT = { id: 'code', desc: false }

function HomeLicensesGroupsTable(props: HomeLicensesGroupsTableProps) {
  const { formatMessage } = useIntl()
  const { licensesGroups } = props

  const headers = useMemo(
    () => ({
      code: {
        label: formatMessage(homeLicensingMessages.license),
        id: 'code',
      },
      orderNumber: {
        label: formatMessage(homeLicensingMessages.orderNumber),
        id: 'orderNumber',
      },
      customerPO: {
        label: formatMessage(homeLicensingMessages.purchaseOrder),
        id: 'customerPO',
      },
      created: {
        label: formatMessage(homeLicensingMessages.created),
        id: 'created',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<LicenseType>[] = useMemo(
    () => [
      {
        Header: headers.code.label,
        accessor: 'code',
        Cell: (cell: Cell<LicenseType>) => <CopyCode code={cell.value} />,
        percentWidth: '20%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.orderNumber.label,
        accessor: 'orderNumber',
        percentWidth: '15%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.customerPO.label,
        accessor: 'customerPO',
        percentWidth: '15%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.created.label,
        accessor: 'created',
        percentWidth: '18%',
        Cell: DateCell,
        sortType: sortByDate,
        cellStyles: CellStyles,
      },
      {
        Header: formatMessage(homeLicensingMessages.notes),
        accessor: 'notes',
        percentWidth: '24%',
        Cell: (cell: Cell<LicenseType>) => (
          <AvailableLicenseNote
            original={cell.row.original}
            initialNote={cell.value}
          />
        ),
        cellStyles: CellStyles,
      },
      {
        Header: '',
        accessor: 'enabled',
        percentWidth: '8%',
        Cell: (cell: Cell<LicenseType>) => (
          <ApplyLicense original={cell.row.original} />
        ),
        cellStyles: HomeLicensesApplyCellStyles,
      },
    ],
    [
      formatMessage,
      headers.code.label,
      headers.created.label,
      headers.customerPO.label,
      headers.orderNumber.label,
    ]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const [sortBy, setSortBy] = useState<SortingRule<LicenseType>>(DEFAULT_SORT)

  return (
    <>
      {licensesGroups.map((licenses) => (
        <HomeLicensesGroup
          key={licenses[0].id}
          columns={columns}
          data={licenses}
          defaultSortBy={DEFAULT_SORT}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
          headers={headers}
        />
      ))}
    </>
  )
}

export default memo(HomeLicensesGroupsTable)
